import { withDriveToken } from '../files/drive';
import { EMAIL_FONT_STACK, photoCaption, renderBodyHtml } from './mailSend';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Gmail draft creation (unification spike for the Outbound Tracker).
 *
 * The contract mirrors the tracker's compliance posture exactly: the app
 * builds a fully formatted DRAFT in the signed-in person's own Gmail and
 * never sends anything. The person reviews and sends 1:1 in Gmail.
 *
 * Uses the same Google credential as Drive uploads, through withDriveToken:
 * the session token first, then one minted from the stored refresh token.
 *
 * gmail.compose no longer rides along at sign-in. Since 12 September it is
 * granted only by the explicit connect flow (see AuthProvider), so a person
 * who has never connected gets 'drive_reconnect_required' here rather than a
 * silent failure — the same code this path already handled.
 */

const GMAIL_DRAFTS = 'https://gmail.googleapis.com/gmail/v1/users/me/drafts';

function fail(message: string): never {
  throw new Error(message);
}

/**
 * The tracker's house style: Trebuchet-first (the same stack the send path
 * uses), working links, bullets, the marks rendered, and the person's
 * signature below. Built by the same renderer as a Hub-sent email so a draft
 * opened in Gmail already carries the bold and the links the writer typed —
 * and so typed text is escaped here too, which the old version never did.
 */
export function draftHtml(body: string, signatureHtml: string): string {
  // A draft carries no inline parts, so a photo mark is written out as its
  // caption in brackets — the person sees where the picture goes and drops
  // it in themselves in Gmail. Photos travel only on the send-as-Alex path.
  const withoutPhotos = body.replace(
    /!\[([^[\]\n]*)\]\((?:cid:[A-Za-z0-9._@-]+|(?:photo|drive):[^()\n]+)\)/g,
    (_m, rawCaption: string) => {
      const { alt } = photoCaption(rawCaption);
      return alt === '' ? '[Photo]' : `[Photo: ${alt}]`;
    },
  );
  return (
    `<div style="font-family: ${EMAIL_FONT_STACK}; font-size: 13px; color: #000;">` +
    renderBodyHtml(withoutPhotos) +
    (signatureHtml.trim() === '' ? '' : `<br><br>${signatureHtml}`) +
    `</div>`
  );
}

/** RFC 2822 message, base64url-encoded the way the Gmail API expects. */
export function buildDraftMime(to: string, subject: string, html: string): string {
  const mime = [
    'Content-Type: text/html; charset=UTF-8',
    'MIME-Version: 1.0',
    `To: ${to}`,
    `Subject: ${subject}`,
    '',
    html,
  ].join('\r\n');
  // btoa handles Latin-1 only; route through UTF-8 bytes first, then make
  // the encoding URL-safe as the API requires.
  const utf8 = new TextEncoder().encode(mime);
  let binary = '';
  utf8.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Creates the draft in the caller's own Gmail. Returns the draft id. */
export async function createGmailDraft(
  client: SupabaseClient,
  input: { to: string; subject: string; body: string; signatureHtml: string },
): Promise<string> {
  const raw = buildDraftMime(input.to, input.subject, draftHtml(input.body, input.signatureHtml));
  // Gmail rides the same Google credential as Drive, so it gets the same
  // mint-and-retry treatment rather than its own expiry story.
  const response = await withDriveToken(client, (token) =>
    fetch(GMAIL_DRAFTS, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message: { raw } }),
    }).then((r) => {
      // withDriveToken only retries on this exact code, so a refused token has
      // to surface as one for the fallback to engage.
      if (r.status === 401 || r.status === 403) {
        fail('drive_reconnect_required');
      }
      return r;
    }),
  );

  if (response.status === 401 || response.status === 403) {
    fail('drive_reconnect_required');
  }
  if (!response.ok) {
    fail(`gmail_error_${response.status}`);
  }
  const created = (await response.json()) as { id?: string };
  return created.id ?? '';
}
